import { useEffect } from "react";
import { useAuthStore } from "@/shared/stores/authStore";
import { useWorkspaceStore } from "@/shared/stores/workspace-store";
import { api } from "@/shared/services/api";

interface CurrentUser {
  id: number;
  email: string;
  name?: string;
  role: "admin" | "curator" | "basic" | "slack_bot" | "limited";
  is_active: boolean;
  is_verified: boolean;
  is_anonymous_user?: boolean;
}

/**
 * Auth initialization hook.
 * Validates the current session by fetching /api/me on mount.
 * Called once in ProtectedRoute (before the loading spinner check).
 *
 * Two auth paths are supported:
 *  1. Bearer-token auth  — token exists in the Zustand store (normal login /
 *     OAuth callback via OAuthCallbackPage). The api ky-instance injects it
 *     as `Authorization: Bearer <token>`.
 *  2. Cookie-based auth  — no Zustand token, but the backend set an HttpOnly
 *     `session` cookie (e.g. Google SSO callback redirected here). The browser
 *     sends the cookie automatically because `api` uses `credentials: "include"`.
 *
 * In both cases GET /api/me returns the current user or 401 → clear auth.
 */
export function useAuthInit(): void {
  const setUser = useAuthStore((s) => s.setUser);
  const setLoading = useAuthStore((s) => s.setLoading);

  useEffect(() => {
    setLoading(true);

    api
      .get("me")
      .json<CurrentUser>()
      .then((user) => {
        // Keep the existing token (Authorization header) if present; fall back
        // to empty string so setUser only needs to be called once.
        const token = useAuthStore.getState().token ?? "";
        setUser(user, token);
      })
      .catch(() => {
        // 401 / network error — clear all auth state so ProtectedRoute
        // redirects to login. logout() sets isLoading: false internally.
        useAuthStore.getState().logout();
      });
    // Only run on mount — dependency array intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Minimal identity view for pages that gate on roles / org (PIKB admin pages).
 * Derived from the real auth + workspace stores; `admin` maps to the global/system admin roles
 * those pages check. The backend still enforces access — this only drives UI redirects.
 */
export function useAuth(): { user: { id: string; email: string; roles: string[]; orgId: string } | null } {
  const user = useAuthStore((s) => s.user);
  const orgId = useWorkspaceStore((s) => s.currentWorkspaceId) ?? "";
  if (!user) return { user: null };
  const roles = user.role === "admin" ? ["admin", "global_admin", "system_admin"] : [user.role];
  return { user: { id: String(user.id), email: user.email, roles, orgId } };
}

export { useAuthStore };
